<template>
  <div data-dynatrace="product-price" data-test-id="product-pricing">
    <span
      class="gap-x-1 i-flex"
      :class="[classWrapper, showOmnibusDetails ? 'ws-nowrap' : 'wrap', { 'f-col gap-y-0.5': block }]"
    >
      <template v-if="isRange && !showDiscountedRanges">
        <span :class="classCurrent">{{ current }}</span>
      </template>
      <template v-else>
        <component
          :is="!isVisuallyDiscounted ? 'span' : 'del'"
          v-if="(!hideStrikethroughPrice || !isVisuallyDiscounted) && !suppressSaleStyling"
          v-style:c="originalPriceColor"
          :class="!isVisuallyDiscounted ? classCurrent : brandClasses.original"
        >
          <span v-if="isDiscounted" class="sr-only">
            {{ isOmnibus ? $t.previousDiscountedPrice : $t.originalPrice }}:
          </span>
          {{ isOmnibus ? historicBest : original }}
        </component>
        <del v-if="price.previousBestSalesPrice && !suppressSaleStyling" :class="!isVisuallyDiscounted ? classCurrent : brandClasses.original">
          <span class="sr-only">{{ $t.previousDiscountedPrice }}:</span>
          {{ formatSinglePrice(price.previousBestSalesPrice) }}
        </del>
        <ins
          v-if="isVisuallyDiscounted"
          v-style:c="salePriceColor"
          :class="[classCurrent, { [brandClasses.sale]: !hideStrikethroughPrice }]"
          style="text-decoration: none"
        >
          <span class="sr-only">
            {{ price.previousBestSalesPrice || isOmnibus ? $t.currentDiscountedPrice : $t.discountedPrice }}:
          </span>
          {{ current }}
          <template v-if="showDiscountPercentage && !isRange">
            ({{ isOmnibus ? historicDiscountPercentage : discountPercentage }})
          </template>
        </ins>
        <span v-if="suppressSaleStyling" class="fw-normal" :class="classCurrent">{{ current }}</span>
      </template>
      <vf-tooltip
        v-if="showSalePriceTooltip && price.salesPriceActiveDate && isVisuallyDiscounted"
        v-style:c="suppressSaleStyling ? undefined : salePriceColor"
        class="-ml-1"
      >
        <template #trigger="{ opened }">
          <vf-icon
            :class="brandClasses.sale"
            :name="opened ? 'info-filled' : 'info'"
          />
        </template>
        {{
          replaceAll(
            $t.salesPriceActiveDateInfo,
            { date: formatDate(new Date(price.salesPriceActiveDate!), 'MMMM YYYY') },
          )
        }}
      </vf-tooltip>
    </span>
    <transition name="slide">
      <div v-if="isOmnibus && showOmnibusDetails">
        <div class="mt-1 w-fit flex gap-x-1 text-xs fw-bold">
          <span class="fw-normal">{{ $t.lowestRecentPrice }}:</span> <del>{{ historicBest }}</del>
          <span v-if="showDiscountPercentage" class="c-brand-3">
            {{ historicDiscountPercentage }}
          </span>
        </div>
        <div v-if="showOmnibusBadge" class="mt-1 w-fit flex gap-x-1 text-xs fw-bold">
          <span class="fw-normal">{{ $t.originalPrice }}:</span> <del>{{ original }}</del>
          <span v-if="showDiscountPercentage" class="c-brand-3">
            {{ discountPercentage }}
          </span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import type { Props } from '#commerce/components/product/pricing/Pricing.vue'
import type { RecommendationStyle } from '#content/contexts/recommendation'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'
import { formatDate } from '@vueuse/core'

const { currency, isDigitalProduct, price, showOmnibusDetails, showSalePriceTooltip } = defineProps<Props>()

const { brandClasses, showDiscountedRanges } = useAppConfig().components.product.pricing
const { $feature, $formatPrice, $t } = useNuxtApp()

const formatDiscount = (percentage: number) =>
  `${$feature.showNegativeDiscountPercentageOnCatalog ? '-' : ''}${percentage}%`

const formatSinglePrice = (value: number) => showOmnibusDetails
  ? $formatPrice(value, currency, { trailingZeroDisplay: 'stripIfInteger' })
  : $formatPrice(value, currency)

const formatPrice = (low: number, high: number) => isDigitalProduct || low === high
  ? low === 0 ? $t.free : formatSinglePrice(low)
  : `${formatSinglePrice(low)} - ${formatSinglePrice(high)}`

const original = computed(() => formatPrice(price.lowOriginal, price.highOriginal))

const current = computed(() => formatPrice(price.lowCurrent, price.highCurrent))

const historicBest = computed(() => formatSinglePrice(price.historicBestPrice ?? 0))

const isDiscounted = computed(() => current.value !== original.value)

const isOmnibus = computed(() => {
  const { historicBestPrice, lowCurrent, lowOriginal } = price
  return (
    $feature.showOmnibusPricing
    && historicBestPrice
    && lowCurrent < lowOriginal
    && lowCurrent < historicBestPrice
  )
})

const isRange = computed(() => price.lowCurrent !== price.highCurrent)

const discountPercentage = computed(() => {
  const { highOriginal, lowCurrent } = price
  const percentage = getPercentage(highOriginal, highOriginal - lowCurrent, 0)
  return formatDiscount(percentage)
})

const historicDiscountPercentage = computed(() => {
  const { percentagePriceDiscount } = price
  return percentagePriceDiscount ? formatDiscount(percentagePriceDiscount) : ''
})

// If Omnibus info is available but current price is not below the 30-day lowest,
// suppress sale/red styling and treat price as non-discounted for display.
const suppressSaleStyling = computed(
  () => $feature.showOmnibusPricing && price.historicBestPrice && price.lowCurrent >= price.historicBestPrice
)

const isVisuallyDiscounted = computed(() => isDiscounted.value && !suppressSaleStyling.value)

const { originalPriceColor, salePriceColor } = inject(RecommendationStyleContextKey, {} as RecommendationStyle)
</script>
